import React, { useState } from 'react';
import './DictionaryTest.css';
import { fetchOpenAIText } from '../utils/openaiClient';

export default function DictionaryTest() {
  const [inputWord, setInputWord] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [error, setError] = useState(null);
  const [wordVariants, setWordVariants] = useState([]); // Варианты частей речи
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  const [pendingWord, setPendingWord] = useState('');

  const handleTestWord = async (e) => {
    e.preventDefault();
    if (!inputWord.trim()) return;

    setIsLoading(true);
    setError(null);

    const word = inputWord.trim();
    console.log('🧪 Начало тестирования слова:', word);
    
    // Сначала запрашиваем все возможные части речи
    try {
      const variantsPrompt = `Напиши все возможные части речи со словом или фразой "${word}". Для каждого значения определи:
1. Часть речи (noun, verb, adjective, adverb, phrasal verb, idiom и т.д.)
2. Тип: если это фразовый глагол (phrasal verb) или идиома (idiom), укажи это в поле "type"
3. CEFR уровень (A1, A2, B1, B2, C1, или C2)
4. Русский перевод - ВАЖНО: включи ВСЕ значения и варианты перевода с подробным контекстом в скобках, не сокращай переводы
5. Пример предложения на английском (8-15 слов)

Ответь ТОЛЬКО валидным JSON в формате:
{
  "variants": [
    {
      "partOfSpeech": "noun",
      "type": "regular",
      "level": "A1",
      "translation": "работа (процесс труда, занятие, деятельность)",
      "example": "I have a lot of work to do."
    },
    {
      "partOfSpeech": "phrasal verb",
      "type": "phrasal verb",
      "level": "B1",
      "translation": "работать над чем-то, разрабатывать",
      "example": "We need to work on this project."
    },
    {
      "partOfSpeech": "idiom",
      "type": "idiom",
      "level": "B2",
      "translation": "работать как часы (работать идеально, без сбоев)",
      "example": "The new system works like a charm."
    }
  ]
}`;

      console.log('🔄 Запрос всех вариантов частей речи...');
      
      const content = await fetchOpenAIText({
        prompt: variantsPrompt,
        temperature: 0.7,
        maxTokens: 800,
      });
      
      if (!content) {
        throw new Error('No response from OpenAI API');
      }

      let contentStr = content.trim();
      
      // Парсим варианты
      let jsonStr = contentStr;
      if (jsonStr.startsWith('```json')) {
        jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
      } else if (jsonStr.startsWith('```')) {
        jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
      }
      
      const match = jsonStr.match(/\{[\s\S]*\}/);
      if (match) {
        const parsed = JSON.parse(match[0]);
        
        if (parsed.variants && Array.isArray(parsed.variants) && parsed.variants.length > 0) {
          // Если несколько вариантов - показываем модальное окно для выбора
          if (parsed.variants.length > 1) {
            setWordVariants(parsed.variants);
            setPendingWord(word);
            setShowVariantsModal(true);
            setIsLoading(false);
            setInputWord('');
            return;
          } else {
            // Если только один вариант - добавляем сразу
            const variant = parsed.variants[0];
            addWordToTable(word, variant);
            setInputWord('');
            setIsLoading(false);
            return;
          }
        }
      }
      
      // Если не получилось распарсить варианты, используем старый метод
      console.warn('⚠️ Не удалось получить варианты, используем старый метод');
    } catch (err) {
      console.error('❌ Ошибка при получении вариантов:', err);
      // Продолжаем со старым методом
    }

    // Старый метод (если не получилось получить варианты)
    await handleSingleWord(word);

    try {
      // Прямой запрос к OpenAI - точно как в LeadInActivitiesExercise (хардкод ключа)
      const prompt = `For the English word "${inputWord.trim()}":
1. Determine its CEFR level (A1, A2, B1, B2, C1, or C2) based on word frequency dictionaries
2. Provide its Russian translation (include all common meanings with context in parentheses if needed)
3. Create a natural English example sentence (8-15 words)

Respond ONLY with valid JSON in this format: {"level": "X", "translation": "Y", "example": "Z"}`;

      console.log('🔄 Making request via proxy...');
      
      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 500,
      });
      
      if (!content) {
        throw new Error('No response from OpenAI API');
      }

      let contentStr = content.trim();
      console.log('✅ Raw response received:', contentStr);
      
      // Парсим JSON из ответа
      let level = 'N/A';
      let translation = 'N/A';
      let example = 'N/A';
      
      try {
        // Удаляем markdown блоки, если есть
        let jsonStr = contentStr;
        if (jsonStr.startsWith('```json')) {
          jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
        } else if (jsonStr.startsWith('```')) {
          jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
        }
        
        // Ищем JSON объект в ответе
        const match = jsonStr.match(/\{[\s\S]*\}/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          
          if (parsed.level && /^[ABC]\d$/.test(parsed.level)) {
            level = parsed.level;
          }
          
          if (parsed.translation) {
            // Обрабатываем перевод - сохраняем все варианты
            let translationText = parsed.translation.trim();
            
            // Если это массив, объединяем через запятую
            if (Array.isArray(parsed.translation)) {
              translationText = parsed.translation
                .map(t => typeof t === 'string' ? t.trim() : String(t).trim())
                .filter(t => t.length > 0)
                .join(', ');
            } else if (typeof translationText === 'string') {
              // Убираем лишние пробелы, но сохраняем все варианты
              translationText = translationText
                .replace(/\s+/g, ' ') // Множественные пробелы в один
                .replace(/,\s*,/g, ',') // Двойные запятые
                .trim();
            }
            
            if (translationText && translationText.length > 0) {
              translation = translationText;
            }
          }
          
          if (parsed.example && parsed.example.trim()) {
            example = parsed.example.trim();
          }
        } else {
          console.warn('⚠️ JSON не найден в ответе, показываем сырой ответ');
          translation = content; // Показываем весь ответ как translation
        }
      } catch (parseErr) {
        console.error('❌ Error parsing JSON:', parseErr);
        console.error('Raw content:', content);
        translation = content; // Показываем весь ответ как translation
      }
      
      const newResult = {
        id: Date.now(),
        word: inputWord.trim(),
        level: level,
        translation: translation,
        example: example,
        rawResponse: content, // Сохраняем сырой ответ для отладки
        timestamp: new Date().toLocaleTimeString(),
        status: (level !== 'N/A' && translation !== 'N/A') ? 'success' : 'partial'
      };

      setTestResults(prev => [newResult, ...prev]);
      setInputWord('');
    } catch (err) {
      const errorMsg = err.message || 'Ошибка при получении данных';
      const errorDetails = `Error: ${errorMsg}\n\nType: ${err.name || 'Unknown'}\n\nStack: ${err.stack || 'No stack trace'}`;
      
      setError(errorMsg);
      console.error('❌ Error testing word:', err);
      console.error('❌ Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack,
        cause: err.cause
      });
      
      // Добавляем результат с ошибкой
      const errorResult = {
        id: Date.now(),
        word: inputWord.trim(),
        rawResponse: errorDetails,
        timestamp: new Date().toLocaleTimeString(),
        status: 'error'
      };
      setTestResults(prev => [errorResult, ...prev]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSingleWord = async (word) => {
    // Старый метод - один запрос для одного значения
    try {
      const prompt = `For the English word or phrase "${word}":
1. Determine if it is a phrasal verb, idiom, or regular word/phrase
2. Determine its CEFR level (A1, A2, B1, B2, C1, or C2) based on word frequency dictionaries
3. Provide its Russian translation - IMPORTANT: include ALL meanings and translation variants with detailed context in parentheses, do NOT shorten translations
4. Create a natural English example sentence (8-15 words)

Respond ONLY with valid JSON in this format: {"type": "regular/phrasal verb/idiom", "level": "X", "translation": "Y", "example": "Z"}`;

      console.log('🔄 Making request via proxy...');
      
      const content = await fetchOpenAIText({
        prompt,
        temperature: 0.7,
        maxTokens: 500,
      });
      
      if (!content) {
        throw new Error('No response from OpenAI API');
      }

      let contentStr = content.trim();
      console.log('✅ Raw response received:', contentStr);
      
      // Парсим JSON из ответа
      let level = 'N/A';
      let translation = 'N/A';
      let example = 'N/A';
      let type = 'regular';
      
      try {
        let jsonStr = contentStr;
        if (jsonStr.startsWith('```json')) {
          jsonStr = jsonStr.replace(/^```json\s*/gi, '').replace(/\s*```/g, '');
        } else if (jsonStr.startsWith('```')) {
          jsonStr = jsonStr.replace(/^```\s*/g, '').replace(/\s*```/g, '');
        }
        
        const match = jsonStr.match(/\{[\s\S]*\}/);
        if (match) {
          const parsed = JSON.parse(match[0]);
          
          if (parsed.type && (parsed.type === 'phrasal verb' || parsed.type === 'idiom')) {
            type = parsed.type;
          }
          
          if (parsed.level && /^[ABC]\d$/.test(parsed.level)) {
            level = parsed.level;
          }
          
          if (parsed.translation) {
            let translationText = parsed.translation.trim();
            if (Array.isArray(parsed.translation)) {
              translationText = parsed.translation
                .map(t => typeof t === 'string' ? t.trim() : String(t).trim())
                .filter(t => t.length > 0)
                .join(', ');
            } else if (typeof translationText === 'string') {
              translationText = translationText
                .replace(/\s+/g, ' ')
                .replace(/,\s*,/g, ',')
                .trim();
            }
            if (translationText && translationText.length > 0) {
              translation = translationText;
            }
          }
          
          if (parsed.example && parsed.example.trim()) {
            example = parsed.example.trim();
          }
        } else {
          translation = content;
        }
      } catch (parseErr) {
        console.error('❌ Error parsing JSON:', parseErr);
        translation = content;
      }
      
      addWordToTable(word, { level, translation, example, type });
      setInputWord('');
    } catch (err) {
      const errorMsg = err.message || 'Ошибка при получении данных';
      setError(errorMsg);
      console.error('❌ Error testing word:', err);
      
      const errorResult = {
        id: Date.now(),
        word: word,
        level: 'ERROR',
        translation: errorMsg,
        example: 'N/A',
        timestamp: new Date().toLocaleTimeString(),
        status: 'error'
      };
      setTestResults(prev => [errorResult, ...prev]);
    }
  };

  const addWordToTable = (word, variant) => {
    const newResult = {
      id: Date.now(),
      word: word,
      level: variant.level || 'N/A',
      translation: variant.translation || 'N/A',
      example: variant.example || 'N/A',
      partOfSpeech: variant.partOfSpeech || '',
      type: variant.type || 'regular',
      timestamp: new Date().toLocaleTimeString(),
      status: (variant.level && variant.level !== 'N/A' && variant.translation && variant.translation !== 'N/A') ? 'success' : 'partial'
    };

    setTestResults(prev => [newResult, ...prev]);
  };

  const handleSelectVariant = (variant) => {
    addWordToTable(pendingWord, variant);
    setShowVariantsModal(false);
    setWordVariants([]);
    setPendingWord('');
  };

  const handleSelectAllVariants = () => {
    wordVariants.forEach(variant => {
      addWordToTable(pendingWord, variant);
    });
    setShowVariantsModal(false);
    setWordVariants([]);
    setPendingWord('');
  };

  const handleClearResults = () => {
    setTestResults([]);
    setError(null);
  };

  return (
    <div className="dictionary-test-container">
      <div className="dictionary-test-header">
        <h1>Тестирование словаря</h1>
        <p>Введите слово или фразу для проверки работы OpenAI API</p>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          API ключ: ✅ Настроен на сервере (безопасно)
        </p>
      </div>

      <div className="dictionary-test-input-section">
        <form onSubmit={handleTestWord} className="dictionary-test-form">
          <input
            type="text"
            value={inputWord}
            onChange={(e) => setInputWord(e.target.value)}
            placeholder="Введите слово или фразу..."
            className="dictionary-test-input"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="dictionary-test-button"
            disabled={isLoading || !inputWord.trim()}
          >
            {isLoading ? 'Загрузка...' : 'Тестировать'}
          </button>
        </form>
        
        {error && (
          <div className="dictionary-test-error">
            Ошибка: {error}
          </div>
        )}

        {testResults.length > 0 && (
          <button
            onClick={handleClearResults}
            className="dictionary-test-clear-button"
          >
            Очистить результаты
          </button>
        )}
      </div>

      <div className="dictionary-test-table-container">
        {testResults.length > 0 ? (
          <div className="dictionary-test-table-wrapper">
            <table className="dictionary-test-table">
              <thead>
                <tr>
                  <th>Word</th>
                  <th>Part of Speech</th>
                  <th>Level</th>
                  <th>Translation</th>
                  <th>Example</th>
                  <th>Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {testResults.map(result => (
                  <tr key={result.id} className={result.status === 'error' ? 'dictionary-test-row-error' : ''}>
                    <td>
                      <strong>{result.word}</strong>
                      {result.type && result.type !== 'regular' && (
                        <span className={`dictionary-test-type-badge dictionary-test-type-${result.type.replace(/\s+/g, '-').toLowerCase()}`} style={{ marginLeft: '8px' }}>
                          {result.type === 'phrasal verb' ? 'Фраз. глагол' : result.type === 'idiom' ? 'Идиома' : result.type}
                        </span>
                      )}
                    </td>
                    <td>
                      {result.partOfSpeech ? (
                        <span className="dictionary-test-pos-badge">
                          {result.partOfSpeech}
                        </span>
                      ) : (
                        <span style={{ color: '#999' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={`dictionary-test-level-badge dictionary-test-level-${(result.level || 'na').toLowerCase()}`}>
                        {result.level}
                      </span>
                    </td>
                    <td>{result.translation}</td>
                    <td>{result.example}</td>
                    <td style={{ fontSize: '12px', color: '#666' }}>
                      {result.timestamp}
                    </td>
                    <td>
                      <span className={`dictionary-test-status-badge dictionary-test-status-${result.status || 'partial'}`}>
                        {result.status === 'success' ? '✅' : result.status === 'error' ? '❌' : '⚠️'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="dictionary-test-empty">
            <p>Введите слово для тестирования</p>
          </div>
        )}
      </div>

      {/* Модальное окно для выбора варианта части речи */}
      {showVariantsModal && wordVariants.length > 0 && (
        <div className="dictionary-test-modal-overlay" onClick={() => setShowVariantsModal(false)}>
          <div className="dictionary-test-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dictionary-test-modal-header">
              <h2>Выберите вариант для слова "{pendingWord}"</h2>
              <button 
                className="dictionary-test-modal-close"
                onClick={() => setShowVariantsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="dictionary-test-modal-content">
              <div className="dictionary-test-variants-list">
                {wordVariants.map((variant, index) => (
                  <div 
                    key={index} 
                    className="dictionary-test-variant-item"
                    onClick={() => handleSelectVariant(variant)}
                  >
                    <div className="dictionary-test-variant-header">
                      {variant.type && variant.type !== 'regular' && (
                        <span className={`dictionary-test-type-badge dictionary-test-type-${variant.type.replace(/\s+/g, '-').toLowerCase()}`}>
                          {variant.type === 'phrasal verb' ? 'Фраз. глагол' : variant.type === 'idiom' ? 'Идиома' : variant.type}
                        </span>
                      )}
                      <span className="dictionary-test-pos-badge">{variant.partOfSpeech || 'N/A'}</span>
                      <span className={`dictionary-test-level-badge dictionary-test-level-${(variant.level || 'na').toLowerCase()}`}>
                        {variant.level || 'N/A'}
                      </span>
                    </div>
                    <div className="dictionary-test-variant-translation">
                      <strong>Перевод:</strong> {variant.translation || 'N/A'}
                    </div>
                    <div className="dictionary-test-variant-example">
                      <strong>Пример:</strong> {variant.example || 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="dictionary-test-modal-actions">
                <button 
                  className="dictionary-test-button dictionary-test-select-all-button"
                  onClick={handleSelectAllVariants}
                >
                  Добавить все варианты
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
